import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

// material-ui
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// project imports
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';

// icons
import { UpOutlined, DownOutlined } from '@ant-design/icons';

// ==============================|| NAVIGATION - COLLAPSE ITEM ||============================== //

function NavCollapse({ menu, level }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  const isChildSelected = useMemo(() => {
    const matchChildren = (children = []) =>
      children.some((child) => {
        if (child.type === 'item') {
          return !!matchPath({ path: child?.link ? child.link : child.url, end: false }, pathname);
        }
        if (child.type === 'collapse') {
          return matchChildren(child.children);
        }
        return false;
      });

    return matchChildren(menu.children);
  }, [menu.children, pathname]);

  const [open, setOpen] = useState(isChildSelected);

  useEffect(() => {
    if (isChildSelected) {
      setOpen(true);
    }
  }, [isChildSelected]);

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const Icon = menu.icon;
  const itemIcon = menu.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem'
      }}
    />
  ) : (
    false
  );

  const textColor = 'text.primary';
  const iconSelectedColor = 'primary.main';

  const navCollapse = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Collapse Children
          </Typography>
        );
    }
  });

  return (
    <>
      <ListItemButton
        selected={isChildSelected}
        onClick={handleClick}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'primary.lighter' },
            '&.Mui-selected': {
              bgcolor: 'primary.lighter',
              borderRight: '2px solid',
              borderColor: 'primary.main',
              color: iconSelectedColor,
              '&:hover': { color: iconSelectedColor, bgcolor: 'primary.lighter' }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isChildSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'secondary.lighter' }
              }),
              ...(!drawerOpen &&
                isChildSelected && {
                  bgcolor: 'primary.lighter',
                  '&:hover': { bgcolor: 'primary.lighter' }
                })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isChildSelected ? iconSelectedColor : textColor }}>
                {menu.title}
              </Typography>
            }
          />
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (open ? <UpOutlined /> : <DownOutlined />)}
      </ListItemButton>
      <Collapse in={open && drawerOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {navCollapse}
        </List>
      </Collapse>
    </>
  );
}

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;
